//import { sign, verify } from "jsonwebtoken";
import { Ref, watch } from 'vue'

// Planificacion para V2:
// - Migrar a IndexedDB para evitar limitaciones de cookies.
// - Implementar NDMSecretGuard para fragmentar y distribuir datos sensibles en múltiples cookies.
// - Añadir compresión de datos antes de guardarlos para optimizar espacio.

// Clase para manejar información sensible en el contexto del navegador. Utiliza JWT para firmar y verificar los datos.

class SimpleIDB {
  private dbName = 'NDM_DB'
  private storeName: string
  private db?: IDBDatabase

  constructor(storeName: string) {
    this.storeName = storeName
  }

  async init() {
    return new Promise<void>((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1)

      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName)
        }
      }

      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      request.onerror = () => reject(request.error)
    })
  }

  // avoid NotFoundError by returning null
  async getAll(): Promise<Record<string, any>> {
    return new Promise((resolve, reject) => {
      if (!this.db) return resolve({})

      let tx = null
      try {
        tx = this.db.transaction(this.storeName, 'readonly')
      } catch (e) {
        return resolve({})
      }
      const store = tx.objectStore(this.storeName)
      const request = store.getAllKeys()

      request.onsuccess = async () => {
        const data: Record<string, any> = {}
        for (const key of request.result) {
          data[key as string] = await this.get(key as string)
        }
        resolve(data)
      }

      request.onerror = () => reject(request.error)
    })
  }

  async get(key: string) {
    return new Promise<any>((resolve) => {
      if (!this.db) return resolve(null)
      const tx = this.db.transaction(this.storeName, 'readonly')
      const store = tx.objectStore(this.storeName)
      const req = store.get(key)
      req.onsuccess = () => resolve(req.result ?? null)
    })
  }

  async set(key: string, value: any) {
    return new Promise<void>((resolve) => {
      if (!this.db) return resolve()
      // Use try to prevent
      // (in promise) DOMException: Failed to execute 'put' on 'IDBObjectStore': #<Object> could not be cloned.
      try {
        const tx = this.db.transaction(this.storeName, 'readwrite')
        tx.objectStore(this.storeName).put(value, key)
        tx.oncomplete = () => resolve()
      } catch (e) {
        return resolve()
      }
    })
  }

  async delete(key: string) {
    return new Promise<void>((resolve) => {
      if (!this.db) return resolve()
      const tx = this.db.transaction(this.storeName, 'readwrite')
      tx.objectStore(this.storeName).delete(key)
      tx.oncomplete = () => resolve()
    })
  }
}

class NDMPrivateGuard {
  private name: string
  private savedData: Record<string, any> = {}
  private db: SimpleIDB

  constructor(name?: string) {
    this.name = name || 'PvGData'
    this.db = new SimpleIDB(this.name)
  }

  async startup() {
    await this.db.init()
    this.savedData = await this.db.getAll()
  }

  public addData(id: string, value: any) {
    this.savedData[id] = value
    this.db.set(id, value)
  }

  public getData(id: string) {
    return this.savedData[id] ?? null
  }

  public deleteData(id: string) {
    delete this.savedData[id]
    this.db.delete(id)
  }
}

// Clase para manejar cookies en el contexto del navegador. No modifica los datos para guardarlos o leerlos.
class NDMPublicGuard {
  private name: string
  private savedData: Record<string, any> = {}
  private db: SimpleIDB

  constructor(name?: string) {
    this.name = name || 'PbGData'
    this.db = new SimpleIDB(this.name)
  }

  async startup() {
    await this.db.init()
    this.savedData = await this.db.getAll()
  }

  public addData(id: string, value: any) {
    this.savedData[id] = value
    this.db.set(id, value)
  }

  public getData(id: string) {
    return this.savedData[id] ?? null
  }

  public deleteData(id: string) {
    delete this.savedData[id]
    this.db.delete(id)
  }
}

// Futuro: Secret Guard. Ademas de encriptar los datos los fragmenta y reparte en varias cookies con nombres no evidentes.
class NDMSecretGuard {}

interface DataLabel {
  id: string
  dataType: 'static' | 'dynamic'
  dataSecurity: 'private' | 'public'
  dataPointer?: Ref<any>
}

class NavigatorDataManager {
  private privateGuard = new NDMPrivateGuard()
  private publicGuard = new NDMPublicGuard()
  private dataLibrary: Set<DataLabel> = new Set([])

  constructor() {}

  public async startup() {
    await Promise.all([this.privateGuard.startup(), this.publicGuard.startup()])
  }

  public registerData(id: string, dataSecurity: 'private' | 'public', data: any, dataPointer?: Ref<any>) {
    const guard = dataSecurity === 'private' ? this.privateGuard : this.publicGuard
    const existing = guard.getData(id)
    const dataType = dataPointer ? 'dynamic' : 'static'

    this.dataLibrary.add({ id, dataType, dataSecurity, dataPointer })

    if (data || !existing) this.saveData(id, data)

    if (dataType === 'dynamic') {
      watch(dataPointer!, (newValue) => this.saveData(id, newValue), { deep: true })
    }

    return existing
  }

  public updateData(id: string, data: any) {
    const existing = Array.from(this.dataLibrary).find((d) => d.id === id)
    if (!existing) throw new Error(`Data with id ${id} is not registered.`)

    if (existing.dataPointer) {
      existing.dataPointer.value = data
    }
    this.saveData(id, data)
  }

  public deleteData(id: string) {
    const existing = Array.from(this.dataLibrary).find((d) => d.id === id)
    if (!existing) throw new Error(`Data with id ${id} is not registered.`)

    this.dataLibrary.delete(existing)

    existing.dataSecurity === 'private' ? this.privateGuard.deleteData(id) : this.publicGuard.deleteData(id)
  }

  private saveData(id: string, value: any) {
    const existing = Array.from(this.dataLibrary).find((d) => d.id === id)
    if (!existing) throw new Error(`Data with id ${id} is not registered.`)

    existing.dataSecurity === 'private' ? this.privateGuard.addData(id, value) : this.publicGuard.addData(id, value)
  }

  public getData(id: string): any {
    const existing = Array.from(this.dataLibrary).find((d) => d.id === id)
    if (!existing) throw new Error(`Data with id ${id} is not registered.`)

    return existing.dataSecurity === 'private' ? this.privateGuard.getData(id) : this.publicGuard.getData(id)
  }
}

const navigatorDataManager = new NavigatorDataManager()
export default navigatorDataManager
